import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

type Props = {
  // Public URL to an audio file; use files in public/assets/audio to avoid bundler import
  src: string;
  // 0.0 - 1.0
  volume?: number;
  // Whether to loop the music (default true)
  loop?: boolean;
  // Milliseconds of silence to insert before each loop restart (default 0 = gapless)
  loopDelayMs?: number;
  // Seconds into the track to begin playback (and loop back to) instead of 0
  startTime?: number;
  // Optional className for the floating control button
  className?: string;
};

// Simple background music player that respects autoplay restrictions.
// - Starts only after a user gesture.
// - Exposes the internal HTMLAudioElement via ref for dev tools.
const BackgroundMusic = forwardRef<HTMLAudioElement, Props>(
  function BackgroundMusic(
    {
      src,
      volume = 0.4,
      loop = true,
      loopDelayMs = 0,
      startTime = 0,
      className,
    },
    ref,
  ) {
    const clampVolume = (value: number) => Math.min(Math.max(value, 0), 1);

    const audioRef = useRef<HTMLAudioElement | null>(null);
    const fadeFrameRef = useRef<number | null>(null);
    const transitionIdRef = useRef(0);
    const targetVolumeRef = useRef(clampVolume(volume));
    const loopDelayMsRef = useRef(loopDelayMs);
    const loopTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const startTimeRef = useRef(startTime);

    // Expose the underlying HTMLAudioElement to parent (e.g. dev tools)
    useImperativeHandle(ref, () => audioRef.current as HTMLAudioElement, []);
    const [isMuted, setIsMuted] = useState<boolean>(false);
    const [hasInteracted, setHasInteracted] = useState<boolean>(false);
    const [currentSrc, setCurrentSrc] = useState(src);

    const cancelFade = () => {
      if (fadeFrameRef.current !== null) {
        window.cancelAnimationFrame(fadeFrameRef.current);
        fadeFrameRef.current = null;
      }
    };

    const fadeVolume = (
      audio: HTMLAudioElement,
      nextVolume: number,
      durationMs: number,
      transitionId: number,
    ) =>
      new Promise<void>((resolve) => {
        cancelFade();

        const startVolume = clampVolume(audio.volume);
        const safeNextVolume = clampVolume(nextVolume);
        if (durationMs <= 0 || startVolume === safeNextVolume) {
          audio.volume = safeNextVolume;
          resolve();
          return;
        }

        const startTime = performance.now();

        const step = (timestamp: number) => {
          if (transitionIdRef.current !== transitionId) {
            resolve();
            return;
          }

          const elapsed = timestamp - startTime;
          const progress = Math.min(Math.max(elapsed / durationMs, 0), 1);
          audio.volume = clampVolume(
            startVolume + (safeNextVolume - startVolume) * progress,
          );

          if (progress >= 1) {
            fadeFrameRef.current = null;
            resolve();
            return;
          }

          fadeFrameRef.current = window.requestAnimationFrame(step);
        };

        fadeFrameRef.current = window.requestAnimationFrame(step);
      });

    // Initialize audio element once
    useEffect(() => {
      const audio = new Audio();
      audioRef.current = audio;
      audio.preload = "auto";
      audio.loop = false;
      audio.src = currentSrc;
      audio.volume = clampVolume(volume);
      audio.muted = isMuted;
      // Explicitly begin buffering — on mobile browsers `preload="auto"` is
      // only a hint and may be ignored until load() is called.
      audio.load();

      const onEnded = () => {
        if (!loop) return;
        if (loopTimeoutRef.current !== null)
          clearTimeout(loopTimeoutRef.current);
        loopTimeoutRef.current = setTimeout(() => {
          loopTimeoutRef.current = null;
          if (audioRef.current) {
            audioRef.current.currentTime = startTimeRef.current;
            audioRef.current.play().catch(() => {
              /* ignore */
            });
          }
        }, loopDelayMsRef.current);
      };
      audio.addEventListener("ended", onEnded);

      return () => {
        audio.removeEventListener("ended", onEnded);
        if (loopTimeoutRef.current !== null) {
          clearTimeout(loopTimeoutRef.current);
          loopTimeoutRef.current = null;
        }
        cancelFade();
        transitionIdRef.current += 1;
        audio.pause();
        // Do not call audio.src = "" to allow mobile to release gracefully
        audioRef.current = null;
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Pause when hidden and resume when tab/app becomes active again.
    useEffect(() => {
      const tryResume = () => {
        if (document.hidden || isMuted || !hasInteracted) return;
        if (!audioRef.current || !audioRef.current.paused) return;
        void audioRef.current.play().catch(() => {
          /* ignore */
        });
      };

      const onVisibilityChange = () => {
        if (!audioRef.current) return;
        if (document.hidden) {
          audioRef.current.pause();
          return;
        }
        tryResume();
      };

      const onWindowFocus = () => {
        tryResume();
      };

      document.addEventListener("visibilitychange", onVisibilityChange);
      window.addEventListener("focus", onWindowFocus);

      return () => {
        document.removeEventListener("visibilitychange", onVisibilityChange);
        window.removeEventListener("focus", onWindowFocus);
      };
    }, [isMuted, hasInteracted]);

    // React to source changes (e.g., scene changes)
    useEffect(() => {
      if (!audioRef.current) return;
      if (src === currentSrc) return;

      const audio = audioRef.current;
      const transitionId = transitionIdRef.current + 1;
      transitionIdRef.current = transitionId;

      setCurrentSrc(src);

      if (isMuted || !hasInteracted) {
        cancelFade();
        audio.src = src;
        audio.load();
        audio.currentTime = startTimeRef.current;
        audio.volume = clampVolume(targetVolumeRef.current);
        return;
      }

      void (async () => {
        await fadeVolume(audio, 0, 400, transitionId);
        if (transitionIdRef.current !== transitionId) return;

        audio.src = src;
        audio.load();
        audio.currentTime = startTimeRef.current;
        audio.volume = 0;

        await audio.play().catch(() => {
          /* ignore */
        });
        if (transitionIdRef.current !== transitionId) return;

        await fadeVolume(audio, targetVolumeRef.current, 500, transitionId);
      })();
    }, [src, currentSrc, isMuted, hasInteracted]);

    // Persist mute state
    useEffect(() => {
      if (!audioRef.current) return;
      audioRef.current.muted = isMuted;
      if (!isMuted) {
        audioRef.current.volume = clampVolume(targetVolumeRef.current);
      }
      if (isMuted) {
        cancelFade();
        transitionIdRef.current += 1;
        audioRef.current.pause();
      } else if (hasInteracted) {
        void audioRef.current.play().catch(() => {
          /* ignore */
        });
      }
    }, [isMuted, hasInteracted]);

    // Sync loopDelayMs ref so the ended handler picks up the latest value
    useEffect(() => {
      loopDelayMsRef.current = loopDelayMs;
    }, [loopDelayMs]);

    // Sync startTime ref so loop restarts use the latest value
    useEffect(() => {
      startTimeRef.current = startTime;
    }, [startTime]);

    // Persist and apply volume
    useEffect(() => {
      if (!audioRef.current) return;
      const v = clampVolume(volume);
      targetVolumeRef.current = v;
      audioRef.current.volume = v;
    }, [volume]);

    // First user gesture to allow playback
    useEffect(() => {
      if (hasInteracted) return;
      const handler = () => {
        setHasInteracted(true);
        if (!audioRef.current) return;
        if (!isMuted) {
          audioRef.current.currentTime = startTimeRef.current;
          void audioRef.current.play().catch(() => {
            /* ignore */
          });
        }
        window.removeEventListener("pointerdown", handler);
        window.removeEventListener("keydown", handler);
      };
      window.addEventListener("pointerdown", handler, { once: true });
      window.addEventListener("keydown", handler, { once: true });
      return () => {
        window.removeEventListener("pointerdown", handler);
        window.removeEventListener("keydown", handler);
      };
    }, [hasInteracted, isMuted]);

    return <></>;
  },
);

export default BackgroundMusic;
